import React, { useState } from 'react';
import styles from './ProductGallery.module.scss';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft, faChevronRight, faSearchPlus, faTimes } from '@fortawesome/free-solid-svg-icons';

const ProductGallery = ({ product }) => {
  const [mainImage, setMainImage] = useState(0);
  const [showFullscreen, setShowFullscreen] = useState(false);
  const [fullscreenIndex, setFullscreenIndex] = useState(0);
  
  // Получаем все изображения (основное + галерея)
  const allImages = [
    product?.main_image || product?.image || '/api/placeholder/400/400',
    ...(product?.gallery || [])
  ];

  const handleThumbnailClick = (index) => {
    setMainImage(index);
  };

  const handlePrevImage = () => {
    setMainImage(prev => prev === 0 ? allImages.length - 1 : prev - 1);
  };

  const handleNextImage = () => {
    setMainImage(prev => prev === allImages.length - 1 ? 0 : prev + 1);
  };

  const openFullscreen = (index) => {
    setFullscreenIndex(index);
    setShowFullscreen(true);
  };

  const closeFullscreen = () => {
    setShowFullscreen(false);
  };

  const handleFullscreenPrev = () => {
    setFullscreenIndex(prev => prev === 0 ? allImages.length - 1 : prev - 1);
  };

  const handleFullscreenNext = () => {
    setFullscreenIndex(prev => prev === allImages.length - 1 ? 0 : prev + 1);
  };

  const handleKeyDown = (e) => {
    if (!showFullscreen) return;
    
    switch (e.key) {
      case 'Escape':
        closeFullscreen();
        break;
      case 'ArrowLeft':
        handleFullscreenPrev();
        break;
      case 'ArrowRight':
        handleFullscreenNext();
        break;
      default:
        break;
    }
  };

  React.useEffect(() => {
    if (showFullscreen) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'auto';
    };
  }, [showFullscreen]);

  if (!allImages || allImages.length === 0) {
    return (
      <div className={styles.galleryContainer}>
        <div className={styles.placeholder}>
          <p>Изображения отсутствуют</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className={styles.galleryContainer}>
        <div className={styles.mainImageContainer}>
          <img
            src={allImages[mainImage]}
            alt={product?.name}
            className={styles.mainImage}
            onClick={() => openFullscreen(mainImage)}
          />
          <button 
            className={styles.zoomButton}
            onClick={() => openFullscreen(mainImage)}
            title="Увеличить"
          >
            <FontAwesomeIcon icon={faSearchPlus} />
          </button>
          
          {allImages.length > 1 && (
            <>
              <button 
                className={`${styles.navButton} ${styles.prevButton}`}
                onClick={handlePrevImage}
                title="Предыдущее изображение"
              >
                <FontAwesomeIcon icon={faChevronLeft} />
              </button>
              <button 
                className={`${styles.navButton} ${styles.nextButton}`}
                onClick={handleNextImage}
                title="Следующее изображение"
              >
                <FontAwesomeIcon icon={faChevronRight} />
              </button>
            </>
          )}
          
          {allImages.length > 1 && (
            <div className={styles.imageCounter}>
              {mainImage + 1} / {allImages.length}
            </div>
          )}
        </div>

        {allImages.length > 1 && (
          <div className={styles.thumbnails}>
            {allImages.map((image, index) => (
              <button
                key={index}
                className={`${styles.thumbnail} ${index === mainImage ? styles.active : ''}`}
                onClick={() => handleThumbnailClick(index)}
              >
                <img src={image} alt={`${product?.name} ${index + 1}`} />
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Полноэкранный режим */}
      {showFullscreen && (
        <div className={styles.fullscreenOverlay} onClick={closeFullscreen}>
          <div className={styles.fullscreenContainer}>
            <button 
              className={styles.closeButton}
              onClick={closeFullscreen}
              title="Закрыть"
            >
              <FontAwesomeIcon icon={faTimes} />
            </button>
            
            <img
              src={allImages[fullscreenIndex]}
              alt={`${product?.name} ${fullscreenIndex + 1}`}
              className={styles.fullscreenImage}
              onClick={(e) => e.stopPropagation()}
            />
            
            {allImages.length > 1 && (
              <>
                <button 
                  className={`${styles.fullscreenNavButton} ${styles.fullscreenPrev}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleFullscreenPrev();
                  }}
                  title="Предыдущее изображение"
                >
                  <FontAwesomeIcon icon={faChevronLeft} />
                </button>
                <button 
                  className={`${styles.fullscreenNavButton} ${styles.fullscreenNext}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleFullscreenNext();
                  }}
                  title="Следующее изображение"
                >
                  <FontAwesomeIcon icon={faChevronRight} />
                </button>
                
                <div className={styles.fullscreenCounter}>
                  {fullscreenIndex + 1} / {allImages.length}
                </div>
                
                <div className={styles.fullscreenThumbnails}>
                  {allImages.map((image, index) => (
                    <button
                      key={index}
                      className={`${styles.fullscreenThumbnail} ${index === fullscreenIndex ? styles.active : ''}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        setFullscreenIndex(index);
                      }}
                    >
                      <img src={image} alt={`${product?.name} ${index + 1}`} />
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default ProductGallery;